import { DeviceGlobalState, DeviceStateRepresentationType } from "@minervaproject/avsubsystem";
import { CameraIcon, CheckIcon, MicUnmutedIcon, SoundLowFilledIcon } from "@minervaproject/fds";

import { useAppSelector, useObjectSelector } from "hooks/redux_hooks";
import {
  DevicesSelectedMonitoringEventType,
  MonitoringEventTypeType,
} from "shared/types/monitoring_event";
import { selectEffectivePttMode } from "store/selectors/classroom";
import { selectLatestMonitoringEventByUserIdAndType } from "store/selectors/monitoring";

type KindType = "Audio Output" | "Microphone" | "Camera";

const TYPE_TO_ICON_MAP: Record<KindType, JSX.Element> = {
  "Audio Output": <SoundLowFilledIcon width={16} height={16} />,
  Microphone: <MicUnmutedIcon width={16} height={16} color="#676767" />,
  Camera: <CameraIcon width={16} height={16} />,
};

const TYPE_TO_KIND_MAP: Record<KindType, string> = {
  "Audio Output": "audiooutput",
  Microphone: "audioinput",
  Camera: "videoinput",
};

const TYPE_TO_DEVICE_STATE_KEY_MAP: Partial<Record<KindType, string>> = {
  Microphone: "mic_state",
  Camera: "camera_state",
};

function Device({ kind, userId }: { kind: KindType; userId: string }) {
  const latestDevicesSelectedEvent = useObjectSelector(
    selectLatestMonitoringEventByUserIdAndType(userId, MonitoringEventTypeType.DEVICES_SELECTED),
    ["id"],
  ) as DevicesSelectedMonitoringEventType | undefined;
  const devices = latestDevicesSelectedEvent?.contextData?.devicesState?.device_list || [];
  const filteredDevices = devices.filter(
    (device: { kind: string }) => device.kind === TYPE_TO_KIND_MAP[kind],
  );
  const currentDevice = filteredDevices.find((device: { selected: boolean }) => device.selected);

  const deviceStateKey = TYPE_TO_DEVICE_STATE_KEY_MAP[kind] as keyof DeviceStateRepresentationType;
  const deviceState = (latestDevicesSelectedEvent?.contextData?.devicesState?.[deviceStateKey] ||
    null) as DeviceGlobalState | null;

  const pttMode = useAppSelector(selectEffectivePttMode(userId));

  return (
    <div className="mb10" data-testid={`device-${TYPE_TO_KIND_MAP[kind]}`}>
      <h2 className="h2 mxn6">
        {TYPE_TO_ICON_MAP[kind]}
        <span className="ml2">{kind}</span>
      </h2>
      <div className="flex body-s">
        <div className="mr3 my3">Currently in Use: {currentDevice?.label || ""}</div>
        {/* {kind === "Camera" ? (
          <div className="my3">Enabled: {classUser.video_enabled ? "Yes" : "No"}</div>
        ) : null} */}
        {kind === "Microphone" ? <div className="my3">PTT: {pttMode ? "Yes" : "No"}</div> : null}
      </div>
      {filteredDevices.length ? (
        <table className="fds-table">
          <thead>
            <tr>
              <th className="col-6">Device</th>
              <th className="col-3">State</th>
              {/* <th className="col-3">When Connected</th> */}
            </tr>
          </thead>
          <tbody>
            {filteredDevices.map((device) => (
              <tr key={device.id}>
                <td className="col-6">
                  {device === currentDevice ? (
                    <span className="mr2">
                      <CheckIcon />
                    </span>
                  ) : null}
                  {device.label}
                </td>
                <td className="col-3">
                  {device === currentDevice ? deviceState?.request_state || "-" : ""}
                </td>
                {/* <td className="col-3">{device.whenConnected || ""}</td> */}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="my3 body-s">
          No devices found. The user may have denied access or is using an unsupported browser.
        </div>
      )}
    </div>
  );
}

export { Device };
